import React, { ReactNode } from 'react';
import { SpaceBetween, Popover, Icon, Box } from '@amzn/awsui-components-console';
import { useHammerstoneGroups } from 'src/data/redux';

/**
 * Displays a group name with an access indicator (locked/unlocked icon).
 * Shows a popover with access details when the icon is clicked.
 *
 * - If the user has access to the Hammerstone group, an unlocked icon is shown.
 * - If the user does not have access, a locked icon is shown with guidance to contact the group's owners.
 *
 * Optionally renders provided children in place of the group name text.
 */

export default function GroupNameAccess({ groupName, children }: { groupName: string; children?: ReactNode }) {
	const hammerstoneGroups = useHammerstoneGroups();
	if (hammerstoneGroups?.includes(groupName)) {
		return (
			<SpaceBetween direction="horizontal" size="xs">
				<Popover size="small" triggerType="text" header={`You have access to group "${groupName}"`}>
					<Icon size="small" name="unlocked" variant="success" />
				</Popover>
				{children ?? groupName}
			</SpaceBetween>
		);
	} else {
		return (
			<SpaceBetween direction="horizontal" size="xs">
				<Popover
					size="medium"
					triggerType="text"
					header="No access to group"
					content={
						<Box>
							You do not appear to have access to Hammerstone group "{groupName}". Please reach out to the
							group's owners for access or questions about their Hammerstone resources including
							pipelines, activities, and instances.
						</Box>
					}
				>
					<Icon size="small" name="lock-private" variant="warning" />
				</Popover>
				{children ?? groupName}
			</SpaceBetween>
		);
	}
}
